/**
 * Unique source of Firebase resource selection for the web client.
 *
 * Production: named Firestore DB + named Storage bucket (explicit env, no fallback).
 * Emulator: Firestore (default) + Storage emulator (never the São Paulo prod resources).
 */

export const PRODUCTION_FIRESTORE_DATABASE_ID = "fivi360-prod-saopaulo";
export const PRODUCTION_STORAGE_BUCKET = "fivi360-prod-saopaulo";
export const DEFAULT_FIRESTORE_DATABASE_ID = "(default)";

export const DEV_PRODUCTION_GUARDRAIL_MESSAGE =
  "[FIVI] Production Firebase resources cannot be used from development without explicit configuration.";

/**
 * @param {string | undefined | null} value
 * @returns {string}
 */
export function normalizeStorageBucket(value) {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim().replace(/^gs:\/\//i, "").replace(/\/+$/, "");
}

/**
 * @param {string} bucket
 * @returns {string}
 */
export function toStorageBucketUrl(bucket) {
  const normalized = normalizeStorageBucket(bucket);
  return normalized ? `gs://${normalized}` : "";
}

/**
 * @param {NodeJS.ProcessEnv | Record<string, string | undefined>} env
 * @returns {boolean}
 */
export function isClientEmulatorEnabled(env) {
  return (
    env.REACT_APP_USE_FIREBASE_EMULATORS === "true" ||
    env.REACT_APP_USE_FIREBASE_EMULATORS === "true"
  );
}

/**
 * @param {NodeJS.ProcessEnv | Record<string, string | undefined>} env
 * @returns {{
 *   useEmulator: boolean,
 *   firestoreDatabaseId: string,
 *   storageBucket: string,
 *   storageBucketUrl: string,
 * }}
 */
export function resolveFirebaseClientResources(env) {
  const nodeEnv = env.NODE_ENV;
  const useEmulator = isClientEmulatorEnabled(env);
  const configuredDatabaseId = (env.REACT_APP_FIRESTORE_DATABASE_ID || "").trim();
  let storageBucket = normalizeStorageBucket(env.REACT_APP_FIREBASE_STORAGE_BUCKET);

  if (nodeEnv === "production" && useEmulator) {
    throw new Error(
      "[FIVI360] REACT_APP_USE_FIREBASE_EMULATORS cannot be true in production. Set false in .env.production or in CI/CD.",
    );
  }

  if (nodeEnv === "development" && !useEmulator) {
    throw new Error(DEV_PRODUCTION_GUARDRAIL_MESSAGE);
  }

  if (useEmulator) {
    if (!storageBucket) {
      throw new Error(
        "[FIVI360] Incomplete Firebase configuration. Missing variable: REACT_APP_FIREBASE_STORAGE_BUCKET.",
      );
    }

    if (storageBucket === PRODUCTION_STORAGE_BUCKET) {
      storageBucket = "fivi360.appspot.com";
    }

    return {
      useEmulator: true,
      firestoreDatabaseId: DEFAULT_FIRESTORE_DATABASE_ID,
      storageBucket,
      storageBucketUrl: toStorageBucketUrl(storageBucket),
    };
  }

  if (configuredDatabaseId !== PRODUCTION_FIRESTORE_DATABASE_ID) {
    throw new Error(
      configuredDatabaseId
        ? `[FIVI360] Invalid REACT_APP_FIRESTORE_DATABASE_ID "${configuredDatabaseId}". Production must use ${PRODUCTION_FIRESTORE_DATABASE_ID}.`
        : `[FIVI360] REACT_APP_FIRESTORE_DATABASE_ID is required when emulators are disabled (expected ${PRODUCTION_FIRESTORE_DATABASE_ID}).`,
    );
  }

  if (storageBucket !== PRODUCTION_STORAGE_BUCKET) {
    throw new Error(
      storageBucket
        ? `[FIVI360] Invalid REACT_APP_FIREBASE_STORAGE_BUCKET "${storageBucket}". Production must use ${PRODUCTION_STORAGE_BUCKET}.`
        : `[FIVI360] REACT_APP_FIREBASE_STORAGE_BUCKET is required when emulators are disabled (expected ${PRODUCTION_STORAGE_BUCKET}).`,
    );
  }

  return {
    useEmulator: false,
    firestoreDatabaseId: PRODUCTION_FIRESTORE_DATABASE_ID,
    storageBucket: PRODUCTION_STORAGE_BUCKET,
    storageBucketUrl: toStorageBucketUrl(PRODUCTION_STORAGE_BUCKET),
  };
}
